import {
  collection,
  collectionGroup,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  getCountFromServer,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import type { Deeplink, Platform } from '@/types';
import { detectPlatform } from '@/utils/platformDetector';
import { generateDeeplink, generateShortCode } from '@/utils/deeplinkGenerator';
import { validateUrlSafety } from './urlValidator';
import { getWhitelist } from './admin';

const COLLECTION_NAME = 'deeplinks';

// Reserved codes that cannot be used as custom short URLs
const RESERVED_CODES = [
  'dashboard', 'deeplinks', 'profile', 'qr-codes', 'stats', 'pixel', 'settings', 'help',
  'login', 'register', 'forgot-password', 'terms', 'legal', 'privacy', 'cookies',
  'admin', '404', 'api', 'app', 'www', 'mail', 'ftp', 'cdn', 'static', 'assets',
  'p', 'linkpages'
];

export interface CreateDeeplinkInput {
  originalUrl: string;
  name?: string;
  userId: string;
}

export interface UpdateDeeplinkInput {
  name?: string;
  originalUrl?: string;
  code?: string;
}

export interface CodeValidationResult {
  valid: boolean;
  error?: 'invalid_format' | 'reserved' | 'unavailable' | 'too_short' | 'too_long' | 'starts_with_at';
}

// Validate code format and rules
export const validateCode = (code: string): CodeValidationResult => {
  // Check length (6-30 characters)
  if (code.length < 6) {
    return { valid: false, error: 'too_short' };
  }
  if (code.length > 30) {
    return { valid: false, error: 'too_long' };
  }

  // Check for @ prefix (reserved for profiles)
  if (code.startsWith('@')) {
    return { valid: false, error: 'starts_with_at' };
  }

  // Check allowed characters: a-zA-Z0-9-_
  const validPattern = /^[a-zA-Z0-9_-]+$/;
  if (!validPattern.test(code)) {
    return { valid: false, error: 'invalid_format' };
  }

  // Check reserved codes (case-insensitive)
  if (RESERVED_CODES.includes(code.toLowerCase())) {
    return { valid: false, error: 'reserved' };
  }

  return { valid: true };
};

// Check if a code is already in use
export const isCodeAvailable = async (code: string, excludeId?: string): Promise<boolean> => {
  const q = query(collection(db, COLLECTION_NAME), where('code', '==', code));
  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    return true;
  }

  // If excludeId is provided, check if the only match is the excluded deeplink
  if (excludeId && querySnapshot.docs.length === 1) {
    return querySnapshot.docs[0].id === excludeId;
  }

  return false;
};

// Generate a unique short code
const generateUniqueCode = async (): Promise<string> => {
  let code = generateShortCode();
  let attempts = 0;
  const maxAttempts = 10;

  while (!(await isCodeAvailable(code)) && attempts < maxAttempts) {
    code = generateShortCode();
    attempts++;
  }

  if (attempts >= maxAttempts) {
    throw new Error('Failed to generate unique code');
  }

  return code;
};

// Check if a domain is in the whitelist
const checkDomainWhitelist = async (domain: string): Promise<boolean> => {
  try {
    const domains = await getWhitelist();
    const normalized = domain.toLowerCase().replace(/^www\./, '');

    return domains.some((whitelisted) => {
      return normalized === whitelisted || normalized.endsWith('.' + whitelisted);
    });
  } catch {
    // If whitelist is unavailable (e.g. rules not deployed yet), treat as not whitelisted
    return false;
  }
};

// Extract domain from a URL
const extractDomain = (url: string): string => {
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return '';
  }
};

// Compute security fields for a deeplink based on platform and URL
const computeSecurityFields = async (platform: Platform, resolvedUrl: string) => {
  if (platform !== 'generic') {
    return { status: 'active' as const, checkNeeded: false };
  }

  const domain = extractDomain(resolvedUrl);
  const isWhitelisted = await checkDomainWhitelist(domain);

  if (isWhitelisted) {
    return { status: 'active' as const, checkNeeded: false };
  }

  return {
    status: 'active' as const,
    checkNeeded: true,
    toCheck: true,
    checked: 0,
    lastCheck: null,
  };
};

const DEFAULT_DAILY_QUOTA = 10;

// Check if user has reached their daily deeplink creation quota
const checkDailyQuota = async (userId: string): Promise<void> => {
  // Get user's custom quota (or default)
  const userDoc = await getDoc(doc(db, 'users', userId));
  const quota = userDoc.exists()
    ? (userDoc.data().dailyDeeplinkQuota ?? DEFAULT_DAILY_QUOTA)
    : DEFAULT_DAILY_QUOTA;

  // 0 = unlimited
  if (quota === 0) return;

  // Count deeplinks created today by this user
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayTimestamp = Timestamp.fromDate(today);

  const q = query(
    collection(db, COLLECTION_NAME),
    where('userId', '==', userId),
    where('createdAt', '>=', todayTimestamp)
  );
  const countSnap = await getCountFromServer(q);
  const todayCount = countSnap.data().count;

  if (todayCount >= quota) {
    throw new Error(`DAILY_QUOTA_REACHED:${quota}`);
  }
};

// Create a new deeplink
export const createDeeplink = async (input: CreateDeeplinkInput): Promise<Deeplink> => {
  const { originalUrl, name, userId } = input;

  // Check daily quota before proceeding
  await checkDailyQuota(userId);

  // Validate URL safety with Google Safe Browsing
  const safety = await validateUrlSafety(originalUrl);
  if (!safety.safe) {
    throw new Error(`URL_UNSAFE:${safety.threatType}`);
  }

  // Use final URL if redirects were resolved (e.g., bit.ly → spotify.com)
  const resolvedUrl = safety.finalUrl || originalUrl;

  // Detect platform from the resolved URL
  const platform: Platform = detectPlatform(resolvedUrl);

  // Generate deeplink URLs from resolved URL
  const { deepLinkUrl, fallbackUrl } = generateDeeplink(platform, resolvedUrl);

  // Generate unique code
  const code = await generateUniqueCode();

  // Compute security fields
  const securityFields = await computeSecurityFields(platform, resolvedUrl);

  // Create document - store resolved URL as the destination
  const docRef = await addDoc(collection(db, COLLECTION_NAME), {
    userId,
    code,
    originalUrl: resolvedUrl, // Store the resolved URL, not the shortener
    platform,
    deepLinkUrl,
    fallbackUrl,
    name: name || null,
    ...securityFields,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Also create a document with the code as ID for quick lookups
  await updateDoc(docRef, { id: docRef.id });

  const docSnap = await getDoc(docRef);
  return { id: docRef.id, ...docSnap.data() } as Deeplink;
};

// Get a deeplink by ID
export const getDeeplinkById = async (id: string): Promise<Deeplink | null> => {
  const docRef = doc(db, COLLECTION_NAME, id);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return { id: docSnap.id, ...docSnap.data() } as Deeplink;
};

// Get a deeplink by code
export const getDeeplinkByCode = async (code: string): Promise<Deeplink | null> => {
  const q = query(collection(db, COLLECTION_NAME), where('code', '==', code));
  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    return null;
  }

  const doc = querySnapshot.docs[0];
  return { id: doc.id, ...doc.data() } as Deeplink;
};

// Get all deeplinks for a user
export const getUserDeeplinks = async (userId: string): Promise<Deeplink[]> => {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );

  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Deeplink[];
};

// Update a deeplink
export const updateDeeplink = async (
  id: string,
  input: UpdateDeeplinkInput
): Promise<Deeplink> => {
  const docRef = doc(db, COLLECTION_NAME, id);

  const updateData: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };

  if (input.name !== undefined) {
    updateData.name = input.name;
  }

  if (input.originalUrl !== undefined) {
    const platform = detectPlatform(input.originalUrl);
    const { deepLinkUrl, fallbackUrl } = generateDeeplink(platform, input.originalUrl);

    updateData.originalUrl = input.originalUrl;
    updateData.platform = platform;
    updateData.deepLinkUrl = deepLinkUrl;
    updateData.fallbackUrl = fallbackUrl;

    // Re-evaluate security fields when URL changes
    const securityFields = await computeSecurityFields(platform, input.originalUrl);
    Object.assign(updateData, securityFields);
  }

  // Handle code change
  if (input.code !== undefined) {
    // Validate the code format
    const validation = validateCode(input.code);
    if (!validation.valid) {
      throw new Error(`Invalid code: ${validation.error}`);
    }

    // Check availability (excluding the current deeplink)
    const available = await isCodeAvailable(input.code, id);
    if (!available) {
      throw new Error('Code is already in use');
    }

    updateData.code = input.code;
  }

  await updateDoc(docRef, updateData);

  const docSnap = await getDoc(docRef);
  return { id: docSnap.id, ...docSnap.data() } as Deeplink;
};

// Toggle deeplink disabled/active status
export const toggleDeeplinkDisabled = async (id: string): Promise<Deeplink> => {
  const docRef = doc(db, COLLECTION_NAME, id);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Deeplink not found');
  }

  const current = docSnap.data();
  const newStatus = current.status === 'disabled' ? 'active' : 'disabled';

  await updateDoc(docRef, {
    status: newStatus,
    updatedAt: serverTimestamp(),
  });

  const updated = await getDoc(docRef);
  return { id: updated.id, ...updated.data() } as Deeplink;
};

// Delete a deeplink
export const deleteDeeplink = async (id: string): Promise<void> => {
  const docRef = doc(db, COLLECTION_NAME, id);
  await deleteDoc(docRef);
};

// Get click count for a deeplink from Firestore sub-collection
export const getDeeplinkClicks = async (id: string): Promise<number> => {
  const clicksCol = collection(db, COLLECTION_NAME, id, 'clicks');
  const snapshot = await getCountFromServer(clicksCol);
  return snapshot.data().count;
};

// Quick dashboard stats from Firestore (costs 1 read per count query)
export const getDashboardQuickStats = async (userId: string): Promise<{
  totalClicks: number;
  weekClicks: number;
}> => {
  const clicksGroup = collectionGroup(db, 'clicks');

  const totalQuery = query(clicksGroup, where('user_id', '==', userId), where('timestamp', '>=', ''));

  // Week clicks: timestamp is ISO string, lexicographic comparison works
  const now = new Date();
  const weekStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay() + 1);
  const weekQuery = query(
    clicksGroup,
    where('user_id', '==', userId),
    where('timestamp', '>=', weekStart.toISOString())
  );

  const [totalSnap, weekSnap] = await Promise.all([
    getCountFromServer(totalQuery),
    getCountFromServer(weekQuery),
  ]);

  return {
    totalClicks: totalSnap.data().count,
    weekClicks: weekSnap.data().count,
  };
};
